<script setup lang="ts">
/**
 * `/` — public landing (DESIGN.md: page-as-Markdown, paper-white, one dark
 * accent). Live price/model data comes from the public router endpoints via the
 * catalog store, with graceful fallbacks so the hero never shows NaN/—.
 *
 * Copy is original to Open Gonka (positioning: open, no-lock-in, market-priced),
 * deliberately distinct from competitor marketing.
 */
import { computed, onMounted } from 'vue';
import { RouterLink } from 'vue-router';
import { useCatalogStore } from '../stores/catalog';
import { PUBLIC_PROXY_BASE } from '../api/docs';
import { formatUsd } from '../lib/format';
import TerminalMockup from '../components/landing/TerminalMockup.vue';
import ModelCard from '../components/shared/ModelCard.vue';
import CopyCodeBlock from '../components/docs/CopyCodeBlock.vue';

const catalog = useCatalogStore();

onMounted(() => {
  void catalog.load();
});

const exampleModel = computed(
  () => catalog.models[0]?.id ?? 'Qwen/Qwen3-235B-A22B-Instruct-2507-FP8',
);

const fromPrice = computed(() =>
  catalog.minPer1mUsd != null ? formatPerMillion(catalog.minPer1mUsd) : null,
);

const openRouterPrices: Record<string, { inputPer1mUsd: number; outputPer1mUsd: number; slug: string }> = {
  'Qwen/Qwen3-235B-A22B-Instruct-2507-FP8': {
    slug: 'qwen/qwen3-235b-a22b-2507',
    inputPer1mUsd: 0.09,
    outputPer1mUsd: 0.1,
  },
  'moonshotai/Kimi-K2.6': {
    slug: 'moonshotai/kimi-k2.6',
    inputPer1mUsd: 0.66,
    outputPer1mUsd: 3.41,
  },
  'MiniMaxAI/MiniMax-M2.7': {
    slug: 'minimax/minimax-m2.7',
    inputPer1mUsd: 0.25,
    outputPer1mUsd: 1,
  },
  'deepseek-ai/DeepSeek-V4-Flash-0731': {
    slug: 'deepseek/deepseek-v4-flash-0731',
    inputPer1mUsd: 0.08,
    outputPer1mUsd: 0.18,
  },
  'zai-org/GLM-5.2-FP8': {
    slug: 'z-ai/glm-5.2',
    inputPer1mUsd: 0.97,
    outputPer1mUsd: 3.04,
  },
};

const priceComparison = computed(() => {
  return (catalog.pricing?.models ?? [])
    .filter((r) => r.per_1m_usd != null && openRouterPrices[r.model])
    .slice(0, 3)
    .map((r) => {
      const openRouter = openRouterPrices[r.model];
      const openGonka = r.per_1m_usd ?? 0;
      return {
        id: r.model,
        name: r.display_name ?? displayModelName(r.model),
        openGonkaPer1m: formatPerMillion(openGonka),
        openRouterInputPer1m: formatPerMillion(openRouter.inputPer1mUsd),
        openRouterOutputPer1m: formatPerMillion(openRouter.outputPer1mUsd),
        outputMultiple: openGonka > 0 ? Math.max(1, Math.round(openRouter.outputPer1mUsd / openGonka)) : null,
      };
    });
});

function displayModelName(id: string): string {
  return id.includes('/') ? id.slice(id.lastIndexOf('/') + 1) : id;
}

function formatPerMillion(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value > 0 && value < 0.01) {
    return `$${value.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`;
  }
  return formatUsd(value);
}

// Coding-agent onboarding: our hosted setup scripts (and the npx equivalents).
// The Connect flow opens an Open Gonka browser link and provisions a gnk-sk key
// without it ever touching the shell history or the agent chat.
const agents = [
  {
    name: 'Hermes Agent',
    blurb:
      'Point the Hermes coding agent at Open Gonka and pick a model — one command, no manual config files.',
    script: 'curl -fsSL https://opengonka.com/install/hermes-agent.sh | bash',
    npx: 'npx @open-gonka/hermes-setup --connect',
  },
  {
    name: 'OpenClaw',
    blurb:
      'Wire the OpenClaw gateway to Open Gonka and provision a key the agent can use straight away.',
    script: 'curl -fsSL https://opengonka.com/install/openclaw-agent.sh | bash',
    npx: 'npx @open-gonka/openclaw-setup --connect',
  },
];

// Top model cards for the teaser grid: merge pricing + capabilities by id.
const teaserCards = computed(() => {
  const capMap = new Map(catalog.capabilities.map((c) => [c.id, c]));
  const priceMap = new Map((catalog.pricing?.models ?? []).map((p) => [p.model, p]));
  const rows = catalog.models.length > 0 ? catalog.models.map((m) => m.id) : [...priceMap.keys()];
  return rows.slice(0, 3).map((id) => {
    const m = catalog.models.find((x) => x.id === id);
    const c = capMap.get(id);
    const p = priceMap.get(id);
    const short = id.includes('/') ? id.slice(id.lastIndexOf('/') + 1) : id;
    return {
      id,
      name: m?.display_name ?? short,
      per1mUsd: p?.per_1m_usd ?? null,
      contextWindow: c?.context_window ?? m?.context_window ?? null,
      tools: c?.tools_supported ?? false,
      reasoning: c?.reasoning_supported ?? false,
    };
  });
});

const values = [
  {
    k: 'Economics',
    t: 'Cheaper by design',
    d: 'Open compute supply means there is no single vendor margin riding on every token you send.',
  },
  {
    k: 'Independence',
    t: 'Nothing proprietary',
    d: 'Requests and responses stay in standard OpenAI shapes, so you can move your traffic elsewhere whenever you want.',
  },
  {
    k: 'Billing',
    t: 'Pay your way',
    d: 'Begin on trial credit, then top up by card or crypto. You are metered per request, never per seat.',
  },
];

const faqs = [
  {
    q: 'Will my existing OpenAI code work?',
    a: 'If it speaks the OpenAI Chat Completions API, yes. Set the base URL, keep your key handling, swap the model id — streaming, model switching and tool calls all come along.',
  },
  {
    q: 'How does billing work?',
    a: 'New accounts get trial credit. After that you top up by card or crypto, and we debit your balance per request using the token count the upstream model reports — no rounding up to a plan.',
  },
  {
    q: 'Why is this cheaper than a big provider?',
    a: 'Your traffic is served by an open market of operators instead of one company’s data center, so there is no single vendor margin baked into every token.',
  },
  {
    q: 'Can I see what I am spending?',
    a: 'Every request lands in your dashboard with its token count and cost, next to live per-model pricing read straight from the chain.',
  },
];
</script>

<template>
  <!-- ── Hero ──────────────────────────────────────────────────────────── -->
  <section class="px-6">
    <div class="max-w-3xl mx-auto pt-20 sm:pt-28 pb-12 text-center flex flex-col items-center">
      <p class="text-xs font-mono uppercase tracking-[0.2em] text-mute mb-6">
        Open models · one OpenAI-compatible API
      </p>
      <h1 class="font-display text-[32px] sm:text-5xl font-semibold tracking-tight leading-[1.08]">
        Open models, one API,<br class="hidden sm:block" />
        no one in the middle.
      </h1>
      <p class="text-body text-base sm:text-lg mt-5 max-w-xl">
        Open Gonka is an OpenAI-compatible endpoint served by the Gonka network. Keep your SDK,
        change one base URL, and pay per request at rates an open compute market sets — not a
        single vendor.
      </p>

      <div class="mt-8 w-full max-w-3xl rounded-lg border border-hairline bg-surface-soft/70 p-4 text-left">
        <div class="flex items-center justify-between gap-3 border-b border-hairline pb-3">
          <div>
            <p class="text-xs font-mono uppercase tracking-[0.18em] text-mute">Open Gonka vs OpenRouter</p>
            <p class="text-sm text-body mt-1">Same model family, shown as USD per 1M tokens.</p>
          </div>
          <RouterLink to="/models" class="link-inline whitespace-nowrap">All models →</RouterLink>
        </div>
        <div v-if="priceComparison.length > 0" class="mt-3 overflow-x-auto">
          <table class="w-full min-w-[640px] text-sm">
            <thead class="text-xs uppercase tracking-[0.14em] text-mute">
              <tr class="border-b border-hairline">
                <th class="py-2 pr-3 text-left font-normal">Model</th>
                <th class="py-2 px-3 text-left font-normal">Open Gonka</th>
                <th class="py-2 px-3 text-left font-normal">OpenRouter</th>
                <th class="py-2 pl-3 text-left font-normal">Delta</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in priceComparison" :key="row.id" class="border-b border-hairline last:border-0">
                <td class="py-3 pr-3 font-medium text-ink">{{ row.name }}</td>
                <td class="py-3 px-3">
                  <span class="font-display text-xl font-semibold">{{ row.openGonkaPer1m }}</span>
                  <span class="text-xs text-body ml-1">/ 1M</span>
                </td>
                <td class="py-3 px-3 text-body">
                  <span class="text-ink">{{ row.openRouterInputPer1m }}</span> in /
                  <span class="text-ink">{{ row.openRouterOutputPer1m }}</span> out
                </td>
                <td class="py-3 pl-3 text-ok font-medium">
                  <span v-if="row.outputMultiple">~{{ row.outputMultiple.toLocaleString('en-US') }}× lower vs output</span>
                  <span v-else>Live</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-sm text-body mt-3">
          Live prices are loading — accounts start with trial credit and usage is charged per token.
        </p>
      </div>

      <div class="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
        <RouterLink to="/login" class="btn-primary">
          Start building free
          <span aria-hidden="true">→</span>
        </RouterLink>
        <RouterLink
          to="/models"
          class="inline-flex items-center gap-1.5 text-sm font-medium text-ink underline underline-offset-4 decoration-hairline-strong hover:decoration-ink transition"
        >
          View live models
          <span aria-hidden="true">→</span>
        </RouterLink>
      </div>

      <!-- Stat strip (bordered, divided) -->
      <dl
        class="mt-14 w-full max-w-2xl grid grid-cols-1 sm:grid-cols-3 rounded-lg border border-hairline divide-y sm:divide-y-0 sm:divide-x divide-hairline text-center"
      >
        <div class="px-5 py-4">
          <dd class="font-display text-xl font-semibold">
            {{ fromPrice ?? 'Loading' }}
          </dd>
          <dt class="text-xs text-mute mt-1">from / 1M tokens</dt>
        </div>
        <div class="px-5 py-4">
          <dd class="font-display text-xl font-semibold">{{ catalog.modelCount || 'Loading' }}</dd>
          <dt class="text-xs text-mute mt-1">models live</dt>
        </div>
        <div class="px-5 py-4">
          <dd class="font-display text-xl font-semibold">Streaming</dd>
          <dt class="text-xs text-mute mt-1">+ tool calling</dt>
        </div>
      </dl>
    </div>
  </section>

  <!-- ── Why ───────────────────────────────────────────────────────────── -->
  <section class="px-6 py-16 sm:py-section">
    <div class="max-w-5xl mx-auto">
      <p class="text-xs font-mono uppercase tracking-[0.2em] text-mute text-center mb-3">Why Open Gonka</p>
      <h2 class="font-display text-2xl sm:text-3xl font-semibold text-center">Pay the market, not a middleman.</h2>
      <p class="text-body text-center mt-3 max-w-xl mx-auto">
        Capacity comes from independent operators competing to serve your traffic. We wrap it in
        the parts a team actually needs — a stable API, billing, and a log of every request.
      </p>
      <div class="mt-12 grid gap-8 sm:grid-cols-3">
        <div v-for="v in values" :key="v.k">
          <div class="text-xs font-mono uppercase tracking-[0.15em] text-mute">{{ v.k }}</div>
          <h3 class="font-display text-lg font-semibold mt-2">{{ v.t }}</h3>
          <p class="text-body text-sm mt-2 leading-relaxed">{{ v.d }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Drop-in / terminal preview ────────────────────────────────────── -->
  <section class="px-6 pb-16 sm:pb-section">
    <div class="max-w-3xl mx-auto">
      <p class="text-xs font-mono uppercase tracking-[0.2em] text-mute text-center mb-3">Drop-in</p>
      <h2 class="font-display text-2xl sm:text-3xl font-semibold text-center">Nothing new to learn.</h2>
      <p class="text-body text-center mt-3 max-w-xl mx-auto mb-8">
        If your code already speaks OpenAI, it already speaks Open Gonka. Point one base URL at us
        and your requests, streaming, and tool calls keep working.
      </p>
      <TerminalMockup title="bash">
        <pre class="whitespace-pre text-charcoal leading-relaxed"><span class="text-mute"># one base URL, any model</span>
$ curl {{ PUBLIC_PROXY_BASE }}/chat/completions \
    -H <span class="text-ink">"Authorization: Bearer gnk-sk-…"</span> \
    -d '{"model":"{{ exampleModel }}",
         "messages":[{"role":"user","content":"Hello!"}]}'

<span class="text-mute"># → OpenAI-shaped response, billed per token</span>
{ "choices": [{ "message": { "content": "Hi! 👋" } }] }</pre>
      </TerminalMockup>
    </div>
  </section>

  <!-- ── Coding agents ─────────────────────────────────────────────────── -->
  <section class="px-6 pb-16 sm:pb-section">
    <div class="max-w-5xl mx-auto">
      <p class="text-xs font-mono uppercase tracking-[0.2em] text-mute text-center mb-3">
        Coding agents
      </p>
      <h2 class="font-display text-2xl sm:text-3xl font-semibold text-center">
        Wire up your agent in one line.
      </h2>
      <p class="text-body text-center mt-3 max-w-xl mx-auto mb-10">
        Connect <span class="text-ink font-medium">Hermes Agent</span> or
        <span class="text-ink font-medium">OpenClaw</span> with our setup scripts. The Connect flow
        opens an Open Gonka browser link and provisions a <code>gnk-sk-…</code> key for you — it
        never lands in your chat or shell history.
      </p>
      <div class="grid gap-5 md:grid-cols-2">
        <div v-for="a in agents" :key="a.name" class="card p-6 sm:p-7 text-left flex flex-col">
          <h3 class="font-display text-xl font-semibold">{{ a.name }}</h3>
          <p class="text-body text-sm mt-1.5 mb-4 leading-relaxed">{{ a.blurb }}</p>
          <div class="mt-auto">
            <CopyCodeBlock :code="a.script" language="bash" />
            <p class="text-xs text-mute mt-3">
              or with npx:
              <code class="break-all">{{ a.npx }}</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Models & live prices ──────────────────────────────────────────── -->
  <section class="px-6 pb-16 sm:pb-section">
    <div class="max-w-5xl mx-auto">
      <p class="text-xs font-mono uppercase tracking-[0.2em] text-mute mb-3">Models &amp; pricing</p>
      <div class="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <h2 class="font-display text-2xl sm:text-3xl font-semibold">Real models, live pricing.</h2>
          <p class="text-body mt-2">Capabilities and rates are read straight from the chain — what you see is what you’re billed.</p>
        </div>
        <RouterLink
          to="/models"
          class="inline-flex items-center gap-1.5 text-sm font-medium text-ink underline underline-offset-4 decoration-hairline-strong hover:decoration-ink"
        >
          See all models
          <span aria-hidden="true">→</span>
        </RouterLink>
      </div>

      <div v-if="teaserCards.length > 0" class="grid gap-4 sm:grid-cols-3">
        <ModelCard
          v-for="c in teaserCards"
          :key="c.id"
          :name="c.name"
          :id="c.id"
          :per1m-usd="c.per1mUsd"
          :context-window="c.contextWindow"
          :tools="c.tools"
          :reasoning="c.reasoning"
        />
      </div>
      <p v-else class="text-body text-sm">
        Live model data is loading — browse the full
        <RouterLink to="/models" class="link-inline">model catalogue</RouterLink>.
      </p>
    </div>
  </section>

  <!-- ── Billing ────────────────────────────────────────── -->
  <section id="pricing" class="px-6 pb-16 sm:pb-section scroll-mt-20">
    <div class="max-w-5xl mx-auto">
      <p
        class="text-xs font-mono uppercase tracking-[0.2em] text-mute text-center mb-3"
      >
        Billing
      </p>
      <h2 class="font-display text-2xl sm:text-3xl font-semibold text-center">
        No plans. Just usage.
      </h2>
      <p class="text-body text-center mt-3 max-w-xl mx-auto mb-10">
        Nothing to outgrow and no seats to count. Take trial credit to start,
        then pay only for the tokens you actually send.
      </p>
      <div class="grid gap-5 md:grid-cols-3 items-stretch">
        <div class="pricing-card">
          <h3 class="font-display text-xl font-semibold">Trial</h3>
          <p class="text-body text-sm mt-1.5 min-h-[2.5rem]">
            Dropped into your balance the first time you sign in.
          </p>
          <div class="font-display text-3xl font-semibold mt-3">$0</div>
          <RouterLink to="/login" class="btn-secondary w-full mt-5">Create account</RouterLink>
          <div class="border-t border-hairline mt-6 mb-5" />
          <ul class="feature-list">
            <li>Free starter tokens</li>
            <li>Full API + dashboard access</li>
            <li>No card required</li>
          </ul>
        </div>

        <div class="pricing-card-featured">
          <div class="flex items-center justify-between gap-2">
            <h3 class="font-display text-xl font-semibold">Usage</h3>
            <span class="pill bg-ink text-on-primary whitespace-nowrap shrink-0">Most common</span>
          </div>
          <p class="text-body text-sm mt-1.5 min-h-[2.5rem]">
            Per-request billing at live chain rates.
          </p>
          <div class="font-display text-3xl font-semibold mt-3">
            {{ fromPrice ?? "$0.00"
            }}<span class="text-body text-base font-normal"> / 1M</span>
          </div>
          <RouterLink to="/login" class="btn-primary w-full mt-5">Get API key</RouterLink>
          <div class="border-t border-hairline mt-6 mb-5" />
          <ul class="feature-list">
            <li>Card or crypto top-ups</li>
            <li>Per-request cost visibility</li>
            <li>Streaming + tool calling</li>
          </ul>
        </div>

        <div class="pricing-card">
          <h3 class="font-display text-xl font-semibold">Partner</h3>
          <p class="text-body text-sm mt-1.5 min-h-[2.5rem]">
            Referrals and white-label child accounts.
          </p>
          <div class="font-display text-3xl font-semibold mt-3">Custom</div>
          <RouterLink to="/login" class="btn-secondary w-full mt-5">Talk to us</RouterLink>
          <div class="border-t border-hairline mt-6 mb-5" />
          <ul class="feature-list">
            <li>Referral links + analytics</li>
            <li>White-label keys</li>
            <li>Billed to the master account</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- ── FAQ ───────────────────────────────────────────────────────────── -->
  <section class="px-6 pb-16 sm:pb-section">
    <div class="max-w-3xl mx-auto">
      <p class="text-xs font-mono uppercase tracking-[0.2em] text-mute text-center mb-3">FAQ</p>
      <h2 class="font-display text-2xl sm:text-3xl font-semibold text-center mb-8">Questions, answered.</h2>
      <div>
        <div v-for="f in faqs" :key="f.q" class="faq-row">
          <h3 class="font-display text-lg font-medium text-ink">{{ f.q }}</h3>
          <p class="text-body mt-1.5 leading-relaxed">{{ f.a }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Final CTA (the single dark accent) ────────────────────────────── -->
  <section class="px-6 pb-16 sm:pb-section">
    <div class="max-w-5xl mx-auto">
      <div class="card-dark px-8 py-10 sm:px-12 sm:py-14 flex flex-col items-center text-center gap-5">
        <h2 class="font-display text-2xl sm:text-3xl font-semibold">You’re one base URL away.</h2>
        <p class="text-on-dark-mute max-w-xl">
          Create an account, mint an API key, and point your existing OpenAI client at Open Gonka.
        </p>
        <RouterLink to="/login" class="btn-on-dark">
          Create a key
          <span aria-hidden="true">→</span>
        </RouterLink>
      </div>
    </div>
  </section>
</template>
